import type { CapturedNetworkRequest, CaptureResult, Properties } from "posthog-js";

import { isAnalyticsExcludedPath } from "./enabled";

/**
 * CREDENTIAL_QUERY_PARAMS are the query parameters that carry a credential, or a handle on
 * one, somewhere a Mountaya page can be addressed: the share token, the session token and
 * the publishable key the studio surfaces read, the one-time code and the flow id of an Ory
 * self-service link, and the challenges of an OAuth login, consent or logout. Compared
 * without regard to case.
 */
const CREDENTIAL_QUERY_PARAMS: ReadonlySet<string> = new Set([
  "code",
  "consent_challenge",
  "flow",
  "login_challenge",
  "logout_challenge",
  "publishable_key",
  "session_token",
  "share_token",
]);

/**
 * CREDENTIAL_HEADERS are the request and response headers that carry a credential, removed
 * from any network request a session replay captures. Compared without regard to case.
 */
const CREDENTIAL_HEADERS: ReadonlySet<string> = new Set([
  "authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
  "x-session-token",
  "x-share-token",
]);

/**
 * URL_PROPERTY matches the names of the properties that hold an address: the current one,
 * the initial and session-entry ones, the previous pageview's, and every referrer. Matching
 * the suffix rather than listing names keeps a property the SDK adds later covered.
 */
const URL_PROPERTY = /(?:url|referrer)$/i;

/**
 * ABSOLUTE_URL tells an absolute address (a scheme ahead of a colon) from a relative one.
 */
const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:/i;

/**
 * RELATIVE_BASE is the origin a relative address is resolved against so its query can be
 * read. It is never written back: a relative address is returned relative.
 */
const RELATIVE_BASE = "https://relative.invalid";

/**
 * CREDENTIAL_NAMES is the alternation of the CREDENTIAL_QUERY_PARAMS, what a credential
 * parameter is recognized by inside free text. The names hold letters and underscores only, and
 * encoding an address leaves both as they are, so the same names match a parameter written
 * plainly and one percent-encoded into another address.
 */
const CREDENTIAL_NAMES = Array.from(CREDENTIAL_QUERY_PARAMS).join("|");

/**
 * PARAMETER_START is what introduces a parameter: a query's `?`, a pair's `&`, a fragment's
 * `#` or the `;` that ends an HTML-escaped `&amp;`, written plainly or percent-encoded once or
 * more (`%3F`, `%26`, `%2526`, …). An address carried inside another one's query, as a share
 * link is inside a social network's share intent, has its separators encoded.
 */
const PARAMETER_START = "(?:[?&#;]|%(?:25)*(?:3f|26|23))";

/**
 * PLAIN_CREDENTIAL matches a credential parameter whose `=` is written plainly, up to the end of
 * its value: the next pair, the fragment, a quote closing an attribute, a space or a `;`.
 */
const PLAIN_CREDENTIAL = new RegExp(
  `(${PARAMETER_START}(?:${CREDENTIAL_NAMES})=)[^&"#\\s;]*`,
  "gi",
);

/**
 * ENCODED_CREDENTIAL matches a credential parameter whose `=` is percent-encoded (`%3D`,
 * `%253D`, …): a parameter of an address that travels encoded inside another one. Its value
 * ends where that address moves on to its next pair or its fragment, encoded as well, or where
 * a plain value would end. A value is never cut short of its token: the credentials these names
 * carry (tokens, keys, flow ids, one-time codes, challenges) hold no separator of their own.
 */
const ENCODED_CREDENTIAL = new RegExp(
  `(${PARAMETER_START}(?:${CREDENTIAL_NAMES})%(?:25)*3d)(?:(?!%(?:25)*(?:26|23))[^&"#\\s;])*`,
  "gi",
);

/**
 * redactCredentialText blanks the value of every credential parameter (CREDENTIAL_QUERY_PARAMS)
 * a free-form text holds, wherever it sits and however deeply it is percent-encoded: in an
 * address, in an autocaptured element's attributes, inside another parameter's value. The name
 * stays, so the text still reads as what it was. A text with no credential comes back as it was.
 */
export const redactCredentialText = (text: string): string =>
  text.replace(PLAIN_CREDENTIAL, "$1").replace(ENCODED_CREDENTIAL, "$1");

/**
 * carriesCredential reports whether a text holds a credential parameter with a value, in any of
 * the forms redactCredentialText blanks.
 */
export const carriesCredential = (text: string): boolean => redactCredentialText(text) !== text;

/**
 * removeCredentialParams removes the credential parameters of an address's own query, the half
 * of stripCredentialParams that parses it.
 */
const removeCredentialParams = (address: string): string => {
  let parsed: URL;
  try {
    parsed = new URL(address, RELATIVE_BASE);
  } catch {
    const query = address.indexOf("?");

    return query === -1 ? address : address.slice(0, query);
  }

  const credentials = Array.from(parsed.searchParams.keys()).filter((name) =>
    CREDENTIAL_QUERY_PARAMS.has(name.toLowerCase()),
  );
  if (credentials.length === 0) {
    return address;
  }

  for (const name of credentials) {
    parsed.searchParams.delete(name);
  }

  return ABSOLUTE_URL.test(address)
    ? parsed.href
    : `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

/**
 * stripCredentialParams removes every credential parameter (CREDENTIAL_QUERY_PARAMS) from an
 * address and leaves the rest of it as it was. An address with none comes back unchanged, as
 * does a value that is no address at all (PostHog's `$direct` referrer). It fails closed: an
 * address it cannot parse loses its whole query.
 *
 * A credential the address carries one level down, percent-encoded into another parameter's
 * value or written into its fragment, is blanked as well (redactCredentialText): a social
 * network's share intent carries the share link whole in its `url` or `text`, token included,
 * and that is the address a click on it records.
 */
export const stripCredentialParams = (address: string): string =>
  redactCredentialText(removeCredentialParams(address));

/**
 * pathnameOf reads the path of an address, or null when it is no address.
 */
const pathnameOf = (address: string): string | null => {
  try {
    return new URL(address, RELATIVE_BASE).pathname;
  } catch {
    return null;
  }
};

/**
 * SNAPSHOT_DATA is the property a replay batch carries its recording in. The recorder masks a
 * recording as it writes it (maskCredentialAttribute, maskCapturedNetworkRequest and the input
 * mask), so the capture policy passes it on untouched rather than walking a whole DOM snapshot
 * on every batch.
 */
const SNAPSHOT_DATA = "$snapshot_data";

/**
 * isPropertyBag tells a plain object, an autocaptured element's attributes or a nested property
 * bag, from every other value an event can hold.
 */
const isPropertyBag = (value: unknown): value is Properties => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const prototype: unknown = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
};

/**
 * sanitizeValue sanitizes one property's value under its name: an address property loses its
 * credential parameters (stripCredentialParams), every other text has their values blanked
 * (redactCredentialText), and a list or a nested bag is sanitized item by item.
 */
const sanitizeValue = (name: string, value: unknown): unknown => {
  if (typeof value === "string") {
    return URL_PROPERTY.test(name) ? stripCredentialParams(value) : redactCredentialText(value);
  }

  if (Array.isArray(value)) {
    return value.map((item: unknown) => sanitizeValue(name, item));
  }

  return isPropertyBag(value) ? sanitizeProperties(value) : value;
};

/**
 * sanitizeProperties strips the credential parameters from every address a property bag holds
 * and blanks them in every other text it holds, at any depth. An autocaptured click carries the
 * clicked link three times over, in `$elements_chain`, in each `$elements` entry's `attr__href`
 * and, for a link off the page's origin, in `$external_click_url`; the link the share sheet
 * offers for each social network carries the share link percent-encoded in its query.
 */
const sanitizeProperties = (properties: Properties): Properties => {
  const sanitized: Properties = {};
  for (const [name, value] of Object.entries(properties)) {
    sanitized[name] = name === SNAPSHOT_DATA ? value : sanitizeValue(name, value);
  }

  return sanitized;
};

/**
 * sanitizeCapture is the `before_send` of every Mountaya property: no credential leaves
 * through an event.
 *
 * - An event raised on a page analytics never runs on (isAnalyticsExcludedPath) is dropped.
 *   Such a page loads no SDK when it is the first one opened; this covers the one reached by
 *   navigating inside a page the SDK started on. Replay batches are the exception, kept so a
 *   recording does not lose the mutations it needs to play back, and their addresses are
 *   stripped by maskCapturedNetworkRequest instead.
 * - Every address the event carries, the current one, the referrer and their initial and
 *   session-entry twins, in its properties and in the person properties it sets, loses its
 *   credential parameters (stripCredentialParams), so a share link a visitor came from, or an
 *   emailed code, is never stored.
 * - Every other text it carries, at any depth, has their values blanked (redactCredentialText),
 *   plain or percent-encoded: an autocaptured click on a link to a social network's share intent
 *   names the share link, token included, in the element chain and the clicked element's
 *   attributes.
 */
export const sanitizeCapture = (result: CaptureResult | null): CaptureResult | null => {
  if (result === null) {
    return null;
  }

  const current = result.properties.$current_url;
  if (result.event !== "$snapshot" && typeof current === "string") {
    const pathname = pathnameOf(current);
    if (pathname !== null && isAnalyticsExcludedPath(pathname)) {
      return null;
    }
  }

  const sanitized: CaptureResult = {
    ...result,
    properties: sanitizeProperties(result.properties),
  };
  if (result.$set !== undefined) {
    sanitized.$set = sanitizeProperties(result.$set);
  }
  if (result.$set_once !== undefined) {
    sanitized.$set_once = sanitizeProperties(result.$set_once);
  }

  return sanitized;
};

/**
 * scrubHeaders drops the credential headers (CREDENTIAL_HEADERS) from a captured request's
 * or response's headers.
 */
const scrubHeaders = (
  headers: Record<string, string> | undefined,
): Record<string, string> | undefined =>
  headers === undefined
    ? undefined
    : Object.fromEntries(
        Object.entries(headers).filter(([name]) => !CREDENTIAL_HEADERS.has(name.toLowerCase())),
      );

/**
 * maskCapturedNetworkRequest is session replay's address and network mask. The recorder
 * hands it every network request it captures, and also, as `{ name: href }`, every page
 * address it writes into a recording: both lose their credential parameters
 * (stripCredentialParams), and a request its credential headers. Tile requests carry the
 * share token and the publishable key in their query, and a recording's page addresses are
 * the share link itself.
 *
 * A request's and a response's body never come back. Installing this mask takes posthog's
 * own body scrubber out of the chain (the recorder runs `custom(base(request))` in place of
 * `scrub(base(request))`), and the bodies this console exchanges are where credentials live:
 * an Ory self-service submit's password, an AI configuration's provider key, a minted session
 * token. Dropping them here is the second lock behind `recordBody: false` (buildInitOptions),
 * which keeps the project's "capture network payloads" toggle from turning recording on.
 */
export const maskCapturedNetworkRequest = (
  request: CapturedNetworkRequest,
): CapturedNetworkRequest => {
  const { requestBody: _requestBody, responseBody: _responseBody, ...bodiless } = request;
  const masked: CapturedNetworkRequest = {
    ...bodiless,
    name: stripCredentialParams(request.name),
  };
  if (request.requestHeaders !== undefined) {
    masked.requestHeaders = scrubHeaders(request.requestHeaders);
  }
  if (request.responseHeaders !== undefined) {
    masked.responseHeaders = scrubHeaders(request.responseHeaders);
  }

  return masked;
};

/**
 * maskCredentialAttribute is session replay's attribute mask. The recorder hands it every
 * attribute of every element it writes into a recording, those of the first snapshot and of each
 * later change alike, and it blanks the value of every credential parameter the attribute holds
 * (redactCredentialText), plainly or percent-encoded. The share sheet is where that matters: each
 * of its social links carries the share link, token included, encoded into the network's share
 * intent, and its link field holds the share link as its value. Any other attribute comes back
 * as it was.
 */
export const maskCredentialAttribute = (_name: string, value: string): string =>
  redactCredentialText(value);
